import { PropsWithChildren, createContext, useContext } from 'react';

import { Box, LegacySectionLayout } from '@betterment/samba-react';
import { useFeatureFlag } from '@betterment/shared-components';
import { ColorModeControls } from '@betterment/shared-components/src/components/color-mode/ColorModeControls';

import NoticeCollection from '../../InAppMessages/GlobalNotices/NoticeCollection';
import FooterWithErrorBoundary from '../../Shared/components-v2/footer/Footer';
import Sidebar from '../../sidebar/Sidebar';

const WithLayoutContext = createContext(false);

type LayoutPart = 'sidebar' | 'footer' | 'globalNotice';
type LayoutPartConfig = Partial<Record<LayoutPart, boolean>>;
type Props = {
  parts: LayoutPartConfig;
  feature?: string;
};

export const WithLayout: React.FC<PropsWithChildren<Props>> = ({ children, parts }) => {
  const isNested = useContext(WithLayoutContext);

  const shouldDisplayFooter = parts.footer ?? true;
  const shouldDisplaySidebar = parts.sidebar ?? true;
  const shouldDisplayGlobalNotice = parts.globalNotice ?? true;

  const colorModeControlsFlag = useFeatureFlag({
    splitName: 'retail.color_mode_controls_q4_2024_enabled',
    context: 'WithLayout',
  });

  if (isNested) {
    throw new Error('WithLayout cannot be nested inside another WithLayout component');
  }

  return (
    <WithLayoutContext.Provider value={true}>
      <Box
        css={{
          display: 'flex',
          flexDirection: {
            base: 'column',
            xl: 'row',
          },
        }}
        data-js-target="main"
      >
        {shouldDisplaySidebar && <Sidebar />}
        <Box css={{ width: '100%' }}>
          {shouldDisplayGlobalNotice && <NoticeCollection />}
          {children}
          {shouldDisplayFooter && (
            <LegacySectionLayout css={{ position: 'static', bg: 'background' }}>
              <LegacySectionLayout.ContentWrapper>
                <FooterWithErrorBoundary />
                {colorModeControlsFlag.enabled && <ColorModeControls />}
              </LegacySectionLayout.ContentWrapper>
            </LegacySectionLayout>
          )}
        </Box>
      </Box>
    </WithLayoutContext.Provider>
  );
};
